import { useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Upload, CheckCircle2, Clock, Heart } from "lucide-react";
import api from "../lib/api";
import ImageUpload from "../components/ImageUpload";
import { toast, Toaster } from "sonner";

const PRODUCT_TYPES = [
    { id: "sticker", label: "Sticker op maat" },
    { id: "tegel",   label: "Tegeltje met tekst" },
    { id: "cadeau",  label: "Cadeaupakket / set" },
    { id: "overig",  label: "Iets anders" },
];

const BUDGETS = [
    { id: "u15",   label: "Onder € 15" },
    { id: "15-30", label: "€ 15 - 30" },
    { id: "30-50", label: "€ 30 - 50" },
    { id: "50p",   label: "€ 50 of meer" },
    { id: "flex",  label: "Geen idee, verras me" },
];

export default function MaakVoorMijPage() {
    const [form, setForm] = useState({
        name: "", email: "", phone: "",
        product_type: "sticker", occasion: "", deadline: "",
        description: "", budget: "", image_url: "",
        consent: true,
    });
    const [sending, setSending] = useState(false);
    const [done, setDone] = useState(false);
    const update = (k) => (v) => setForm(f => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim() || !form.description.trim()) {
            toast.error("Naam, e-mail en beschrijving zijn verplicht");
            return;
        }
        setSending(true);
        try {
            await api.post("/custom-requests", form);
            setDone(true);
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Versturen mislukt — probeer opnieuw");
        } finally { setSending(false); }
    };

    if (done) {
        return (
            <div data-testid="maak-voor-mij-success" className="min-h-[70vh] grid place-items-center px-6 py-20" style={{ background: "var(--bg)" }}>
                <Toaster />
                <div className="max-w-xl text-center">
                    <div className="w-20 h-20 rounded-full mx-auto flex items-center justify-center" style={{ background: "var(--rose-tint)" }}>
                        <CheckCircle2 size={40} style={{ color: "var(--rose-deep)" }} />
                    </div>
                    <p className="label-tiny mt-6" style={{ color: "var(--rose-deep)" }}>★ Aanvraag ontvangen</p>
                    <h1 className="font-display text-5xl mt-3">We gaan voor je aan de slag.</h1>
                    <p className="mt-5 text-base" style={{ color: "var(--text-2)" }}>
                        Je ontvangt binnen <strong>24 uur</strong> persoonlijk antwoord op
                        <span className="font-mono-dm"> {form.email}</span>. We sturen je een eerste
                        voorstel of stellen een paar gerichte vragen.
                    </p>
                    <div className="mt-8 flex gap-3 justify-center">
                        <Link to="/" className="btn-ghost" data-testid="mvm-back-home">← Naar de winkel</Link>
                        <Link to="/stickers" className="btn-rose" data-testid="mvm-go-shop">
                            <Sparkles size={14} /> Browse stickers
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div data-testid="maak-voor-mij-page" className="min-h-screen" style={{ background: "var(--bg)" }}>
            <Toaster />
            {/* Hero */}
            <section className="hero-warm border-b border-[var(--border-soft)]">
                <div className="max-w-[1100px] mx-auto px-6 lg:px-10 py-16 lg:py-20">
                    <p className="label-tiny" style={{ color: "var(--rose-deep)" }}>★ Maak voor mij</p>
                    <h1 className="font-display text-5xl md:text-6xl mt-3 tracking-tight leading-[1.05]">
                        Een sticker of tegeltje<br />
                        <span className="font-script" style={{ color: "var(--rose-deep)" }}>op maat</span>?
                    </h1>
                    <p className="mt-6 max-w-xl text-base leading-relaxed" style={{ color: "var(--text-2)" }}>
                        Beschrijf wat je in gedachten hebt — eigen logo, naam, spreuk of bedrijfsbelettering
                        die je nergens vindt. Wij ontwerpen en snijden het voor je en sturen <strong>binnen 24 uur</strong>
                        een voorstel met prijs en tijdsindicatie.
                    </p>
                    <div className="mt-4 inline-flex items-start gap-2 px-3 py-2 rounded-xl max-w-xl" style={{ background: "var(--rose-tint)" }} data-testid="mvm-mono-notice">
                        <span className="text-base shrink-0" aria-hidden>🎨</span>
                        <p className="text-xs leading-relaxed" style={{ color: "var(--text)" }}>
                            <strong>Mono vinyl:</strong> stickers &amp; logo&apos;s worden gesneden in één enkele kleur (wit, zwart, rood, geel, groen, paars, goud, zilver, ...) — geen full-colour print.
                        </p>
                    </div>
                    <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm" style={{ color: "var(--text-2)" }}>
                        <span className="inline-flex items-center gap-1.5"><Clock size={14} style={{ color: "var(--rose)" }} /> Antwoord binnen 24u</span>
                        <span className="inline-flex items-center gap-1.5"><CheckCircle2 size={14} style={{ color: "var(--rose)" }} /> Vrijblijvend voorstel</span>
                        <span className="inline-flex items-center gap-1.5"><Heart size={14} style={{ color: "var(--rose)" }} /> Echt op maat</span>
                    </div>
                </div>
            </section>

            {/* Form */}
            <section className="max-w-[1100px] mx-auto px-6 lg:px-10 py-14">
                <form onSubmit={submit} className="card-soft p-8 md:p-10 grid grid-cols-1 md:grid-cols-2 gap-5 bg-white">
                    <div>
                        <label className="label-tiny mb-1.5 block">Je naam *</label>
                        <input data-testid="mvm-name" className="input-flat" value={form.name}
                            onChange={(e) => update("name")(e.target.value)} placeholder="Lotte Vandenberghe" required />
                    </div>
                    <div>
                        <label className="label-tiny mb-1.5 block">E-mail *</label>
                        <input data-testid="mvm-email" type="email" className="input-flat" value={form.email}
                            onChange={(e) => update("email")(e.target.value)} placeholder="lotte@example.com" required />
                    </div>
                    <div>
                        <label className="label-tiny mb-1.5 block">Telefoon (optioneel)</label>
                        <input data-testid="mvm-phone" className="input-flat" value={form.phone}
                            onChange={(e) => update("phone")(e.target.value)} placeholder="+32 ..." />
                    </div>
                    <div>
                        <label className="label-tiny mb-1.5 block">Wat zoek je?</label>
                        <select data-testid="mvm-product" className="input-flat" value={form.product_type}
                            onChange={(e) => update("product_type")(e.target.value)}>
                            {PRODUCT_TYPES.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="label-tiny mb-1.5 block">Gelegenheid (optioneel)</label>
                        <input data-testid="mvm-occasion" className="input-flat" value={form.occasion}
                            onChange={(e) => update("occasion")(e.target.value)} placeholder="bv. moederdag, pensioen, ..." />
                    </div>
                    <div>
                        <label className="label-tiny mb-1.5 block">Tegen wanneer?</label>
                        <input data-testid="mvm-deadline" className="input-flat" value={form.deadline}
                            onChange={(e) => update("deadline")(e.target.value)} placeholder="bv. 15 mei 2026" />
                    </div>
                    <div className="md:col-span-2">
                        <label className="label-tiny mb-1.5 block">Budget (vrijblijvend)</label>
                        <div className="flex flex-wrap gap-2">
                            {BUDGETS.map(b => (
                                <button key={b.id} type="button"
                                    data-testid={`mvm-budget-${b.id}`}
                                    onClick={() => update("budget")(b.id)}
                                    className={`gift-pill ${form.budget === b.id ? "active" : ""}`}>
                                    {b.label}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="md:col-span-2">
                        <label className="label-tiny mb-1.5 block">Vertel ons je idee *</label>
                        <textarea data-testid="mvm-description" required
                            value={form.description}
                            onChange={(e) => update("description")(e.target.value)}
                            rows={6}
                            placeholder="Bv. 'Ik wil een tegeltje met de eerste lijn van het lievelingsliedje van mijn oma, in een romantisch lettertype. Liefst zacht roze.'"
                            className="input-flat resize-y" />
                    </div>
                    <div className="md:col-span-2">
                        <label className="label-tiny mb-1.5 block flex items-center gap-1.5">
                            <Upload size={12} /> Referentie-foto of moodboard (optioneel)
                        </label>
                        <ImageUpload value={form.image_url} onChange={update("image_url")} testId="mvm-image" />
                    </div>
                    <div className="md:col-span-2 flex items-start gap-2 pt-2">
                        <input id="mvm-consent" data-testid="mvm-consent" type="checkbox" checked={form.consent}
                            onChange={(e) => update("consent")(e.target.checked)}
                            className="mt-1 w-4 h-4 accent-[var(--rose-deep)]" />
                        <label htmlFor="mvm-consent" className="text-xs" style={{ color: "var(--text-2)" }}>
                            Ik geef VdS Gift Shop toestemming om mijn aanvraag te bewaren en contact op te nemen voor dit project.
                        </label>
                    </div>
                    <div className="md:col-span-2 flex flex-wrap gap-3 items-center pt-2">
                        <button type="submit" disabled={sending} data-testid="mvm-submit" className="btn-rose disabled:opacity-60">
                            {sending ? "Versturen..." : <><Sparkles size={14} /> Stuur mijn aanvraag →</>}
                        </button>
                        <a
                            href={`https://wa.me/?text=${encodeURIComponent("Hallo VdS Gift Shop, ik wil graag iets op maat. Ik zoek: ")}`}
                            target="_blank" rel="noopener noreferrer"
                            data-testid="mvm-whatsapp"
                            className="btn-ghost inline-flex items-center gap-2">
                            <span className="text-base" aria-hidden>💬</span> Of via WhatsApp
                        </a>
                        <span className="text-xs" style={{ color: "var(--text-3)" }}>
                            Beloofd: binnen 24u krijg je een persoonlijk antwoord.
                        </span>
                    </div>
                </form>
            </section>
        </div>
    );
}
